/**
 * The results screen's match story, as data: the chronicle (@sa/protocol chronicle.ts) laid out
 * on a timeline (three lanes — DIPLO, WAR, LEAD — marks spread so they do not sit on each
 * other), told in words, the handful of key moments, and the per-nation strike numbers.
 *
 * Time reads as the HUD clock did (it counts down): the timeline runs from the full clock on
 * the left to 0:00 on the right, and a moment is the clock reading it happened at (`12:08`).
 * Pure (tested in test/story.test.ts).
 */
import { BALANCE, TICK_HZ, type PlayerId, type PlayerStats } from '@sa/sim';
import { OUT_DECAP, OUT_SURRENDER, OUT_TAKEN, STRIKE_CAPITAL, chronicleWeight, type Chronicle, type ChronicleEntry, type ChronicleKind, type ChronicleTally, type EndMessage } from '@sa/protocol';
import { clock } from './alerts';

export const LANES = ['DIPLO', 'WAR', 'LEAD'] as const;
export type Lane = 0 | 1 | 2;

const LANE_OF: Record<ChronicleKind, Lane> = {
  ally: 0,
  betrayal: 0,
  ceasefire: 0,
  truceBroken: 0,
  gift: 0,
  firstLaunch: 1,
  strike: 1,
  critical: 1,
  recovered: 1,
  out: 1,
  leader: 2,
  domination: 2,
  final: 2,
  end: 2,
};

/** A moment placed on the timeline. */
export interface StoryMark {
  e: ChronicleEntry;
  /** 0..1 along the match: 0 = the full clock, 1 = 0:00 */
  x: number;
  lane: Lane;
  /** 0 / 1: the second row of a lane takes a mark that would sit on its neighbour */
  row: number;
  /** the clock reading, `12:08` */
  at: string;
  text: string;
  weight: number;
}

/** The clock reading at `tick` (`12:08`): time left, like the HUD clock. */
export const clockAt = (tick: number, matchTicks: number) => clock(Math.max(0, matchTicks - tick) / TICK_HZ);

const plural = (n: number, one: string, many = `${one}S`) => `${n} ${n === 1 ? one : many}`;

/**
 * A moment in words: `ALEX BETRAYED SAM`, `VULCAN DESTROYED SAM’S CAPITAL · SAM IS OUT`. A nation
 * named YOU (offline) reads as you: `HALCYON HIT YOUR CAPITAL`, `YOU ARE OUT`, `YOU WIN`.
 */
export function storyText(e: ChronicleEntry, name: (id: PlayerId) => string): string {
  const you = (id: PlayerId) => name(id) === 'YOU';
  const pos = (id: PlayerId) => (you(id) ? 'YOUR' : `${name(id)}’S`);
  const isOut = (id: PlayerId) => `${name(id)} ${you(id) ? 'ARE' : 'IS'} OUT`;
  const A = name(e.a);
  const B = e.b ? name(e.b) : '';
  const f = e.f ?? 0;
  const n = e.n ?? 0;
  switch (e.k) {
    case 'ally':
      return `${A} AND ${B} ALLIED`;
    case 'betrayal':
      return `${A} BETRAYED ${B}`;
    case 'ceasefire':
      return `${A} AND ${B} AGREED A CEASEFIRE`;
    case 'truceBroken':
      return `${A} BROKE THE CEASEFIRE WITH ${B}`;
    case 'gift':
      return `${A} SENT ${B} ${n.toLocaleString('en-US')} CREDITS`;
    case 'firstLaunch':
      return n > 1 ? `${A} FIRED THE FIRST SALVO · ${n} WARHEADS` : `${A} FIRED THE FIRST WARHEAD`;
    case 'strike': {
      const parts = [f & STRIKE_CAPITAL ? `${A} HIT ${pos(e.b)} CAPITAL` : `${A} STRUCK ${B}`, plural(n, 'WARHEAD')];
      if (e.c) parts.push(`${plural(e.c, 'CITY', 'CITIES')} DESTROYED`);
      if (e.p) parts.push(`${e.p}% OF ${pos(e.b)} PEOPLE KILLED`);
      return parts.join(' · ');
    }
    case 'critical':
      return B ? `${B} LEFT ${pos(e.a)} CAPITAL CRITICAL` : `${pos(e.a)} CAPITAL WENT CRITICAL`;
    case 'recovered':
      return `${pos(e.a)} CAPITAL RECOVERED`;
    case 'out':
      if (f & OUT_SURRENDER) return `${A} SURRENDERED`;
      if (f & OUT_TAKEN) return B ? `${B} TOOK ${pos(e.a)} CAPITAL · ${isOut(e.a)}` : `${pos(e.a)} CAPITAL FELL · ${isOut(e.a)}`;
      if (f & OUT_DECAP) return B ? `${B} DESTROYED ${pos(e.a)} CAPITAL · ${isOut(e.a)}` : `${pos(e.a)} CAPITAL FELL · ${isOut(e.a)}`;
      return B ? `${B} WIPED OUT ${A}` : `${A} ${you(e.a) ? 'WERE' : 'WAS'} WIPED OUT`;
    case 'leader':
      return B ? `${A} OVERTOOK ${B} FOR THE LEAD` : `${A} TOOK THE LEAD`;
    case 'domination':
      return `${f & 1 ? `${pos(e.a)} TEAM` : A} HELD ${n}% OF THE LAND · DOMINATION THREAT`;
    case 'final':
      return `FINAL MINUTE · ${A} ${you(e.a) ? 'LEAD' : 'LEADS'} ${B} BY ${n}`;
    case 'end':
      if (!e.a) return 'NO WINNER';
      return `${A}${n > 1 ? ' + TEAM' : ''} ${n > 1 || you(e.a) ? 'WIN' : 'WINS'}`;
  }
}

/**
 * Every chronicle entry on the timeline. In each lane a mark closer than `minGap` (a fraction of
 * the width) to the previous one on its row moves to the lane's other row; when both rows are
 * crowded it takes the one it crowds least and steps right to `minGap` past its neighbour (`x`
 * is where it is drawn; `at` still tells its time) — marks never sit on each other.
 */
export function storyMarks(ch: Chronicle, name: (id: PlayerId) => string, matchTicks: number = BALANCE.matchTicks, minGap = 0.018): StoryMark[] {
  const last: number[][] = LANES.map(() => [-1, -1]);
  return [...ch.entries]
    .sort((a, b) => a.t - b.t)
    .map((e) => {
      const lane = LANE_OF[e.k];
      const x = Math.max(0, Math.min(1, e.t / matchTicks));
      const rows = last[lane];
      let row = rows.findIndex((r) => r < 0 || x - r >= minGap);
      let at = x;
      if (row < 0) {
        row = rows[0] <= rows[1] ? 0 : 1;
        at = Math.min(1, rows[row] + minGap);
      }
      rows[row] = at;
      return { e, x: at, lane, row, at: clockAt(e.t, matchTicks), text: storyText(e, name), weight: chronicleWeight(e) };
    });
}

/** at most this many of one kind among the key moments (a war of strikes still leaves room for the rest) */
const KIND_CAP: Partial<Record<ChronicleKind, number>> = { strike: 2, betrayal: 3, critical: 2, ally: 2, leader: 2, truceBroken: 1, gift: 1, ceasefire: 1, recovered: 1, end: 0 };

/**
 * The `max` moments that matter most, in time order: by weight, plus what touched us (`me`) and the
 * last lead change (the lead that held to the end); the end itself is the header already.
 */
export function keyMoments(marks: readonly StoryMark[], max = 7, me: PlayerId = 0): StoryMark[] {
  const lastLead = [...marks].reverse().find((m) => m.e.k === 'leader');
  const score = (m: StoryMark) => m.weight + (me && (m.e.a === me || m.e.b === me) ? 15 : 0) + (m === lastLead ? 25 : 0);
  const used = new Map<ChronicleKind, number>();
  const picked: StoryMark[] = [];
  for (const m of [...marks].sort((a, b) => score(b) - score(a) || a.e.t - b.e.t)) {
    if (picked.length >= max) break;
    const k = m.e.k;
    if ((used.get(k) ?? 0) >= (KIND_CAP[k] ?? Infinity)) continue;
    used.set(k, (used.get(k) ?? 0) + 1);
    picked.push(m);
  }
  return picked.sort((a, b) => a.e.t - b.e.t);
}

export interface AxisMark {
  x: number;
  label: string;
}

/** The phase lines: weapons release and capitals exposed, where they fall in this match. */
export function phaseMarks(matchTicks: number = BALANCE.matchTicks): (AxisMark & { at: string })[] {
  const P = BALANCE.phases;
  return [
    { tick: P.releaseTick, label: 'RELEASE' },
    { tick: P.capitalsExposedTick, label: 'CAPITALS' },
  ]
    .filter((m) => m.tick > 0 && m.tick < matchTicks)
    .map((m) => ({ x: m.tick / matchTicks, label: m.label, at: clockAt(m.tick, matchTicks) }));
}

/** Clock labels under the timeline: every 5 min (every minute in a short match), full clock … 0:00. */
export function axisTicks(matchTicks: number = BALANCE.matchTicks): AxisMark[] {
  const min = 60 * TICK_HZ;
  const step = matchTicks >= 10 * min ? 5 * min : min;
  const out: AxisMark[] = [];
  for (let t = 0; t < matchTicks - step / 2; t += step) out.push({ x: t / matchTicks, label: clockAt(t, matchTicks) });
  out.push({ x: 1, label: '0:00' });
  return out;
}

// --------------------------------------------------------------------------- numbers

/** One nation's war in numbers (the chronicle's tallies when there are any, else the sim's stats). */
export interface NationNumbers {
  /** ICBMs + SLBMs + bombs */
  fired: number;
  /** of those, shot down by enemy defences */
  intercepted: number;
  /** went off on a living rival's land (null: unknown — no chronicle) */
  onTarget: number | null;
  /** went off on open ground (a fallen nation's land, neutral land, sea): only with a chronicle */
  wasted: number;
  citiesDestroyed: number | null;
  citiesLost: number;
  /** hostile warheads our defences shot down (null: unknown) */
  shotDown: number | null;
  gifted: number;
  criticals: number;
}

export function nationNumbers(end: Pick<EndMessage, 'stats' | 'chronicle'>, id: PlayerId): NationNumbers | null {
  const st: PlayerStats | undefined = end.stats.players.find((p) => p.id === id);
  if (!st) return null;
  const t: ChronicleTally | undefined = end.chronicle?.tallies.find((x) => x.id === id);
  return {
    fired: st.icbmLaunched + st.slbmLaunched + st.bombsDropped,
    intercepted: st.intercepted,
    onTarget: t ? t.onTarget : null,
    wasted: t?.wasted ?? 0,
    citiesDestroyed: t ? t.citiesDestroyed : null,
    citiesLost: st.citiesLost,
    shotDown: t ? t.shotDown : null,
    gifted: t?.gifted ?? 0,
    criticals: st.capitalCriticals ?? 0,
  };
}

/** A row of the numbers block: label, what it means, and one value per nation shown. */
export interface NumberRow {
  label: string;
  title: string;
  values: string[];
}

/**
 * The numbers block for these nations (the humans: you first). Hits are only what landed on a
 * living rival — warheads that fell on a fallen nation's ruins or open ground are WASTED, not
 * hits (and a kill share is not a hit rate: the standings say SHARE). Rows nobody scored on are
 * left out (gifts, criticals, wasted).
 */
export function numberRows(end: Pick<EndMessage, 'stats' | 'chronicle'>, ids: readonly PlayerId[]): NumberRow[] {
  const ns = ids.map((id) => nationNumbers(end, id));
  const v = (f: (n: NationNumbers) => number | null) => ns.map((n) => (n ? fmt(f(n)) : '—'));
  const any = (f: (n: NationNumbers) => number) => ns.some((n) => n && f(n) > 0);
  const told = !!end.chronicle;
  const rows: (NumberRow | null)[] = [
    { label: 'FIRED', title: 'warheads launched or dropped: ICBMs, SLBMs, bombs', values: v((n) => n.fired) },
    { label: 'INTERCEPTED', title: 'of those, shot down by enemy defences', values: v((n) => n.intercepted) },
    told
      ? { label: 'ON TARGET', title: 'went off on a living rival’s land', values: v((n) => n.onTarget) }
      : { label: 'LANDED', title: 'went off (anywhere)', values: ns.map((n, i) => (n ? fmt(end.stats.players.find((p) => p.id === ids[i])?.hits ?? 0) : '—')) },
    told && any((n) => n.wasted) ? { label: 'WASTED', title: 'went off on open ground: a fallen nation’s ruins, neutral land, sea', values: v((n) => n.wasted) } : null,
    told ? { label: 'CITIES DESTROYED', title: 'rival cities your warheads destroyed', values: v((n) => n.citiesDestroyed) } : null,
    { label: 'CITIES LOST', title: 'your cities destroyed', values: v((n) => n.citiesLost) },
    told ? { label: 'SHOT DOWN', title: 'hostile missiles your defences shot down', values: v((n) => n.shotDown) } : null,
    any((n) => n.gifted) ? { label: 'CREDITS GIVEN', title: 'credits sent to other nations', values: v((n) => n.gifted) } : null,
    any((n) => n.criticals) ? { label: 'CAPITAL CRITICAL', title: 'times your capital went critical', values: v((n) => n.criticals) } : null,
  ];
  return rows.filter((r): r is NumberRow => r !== null);
}

const fmt = (x: number | null) => (x === null ? '—' : Math.round(x).toLocaleString('en-US'));
